import { IonCard, IonImg } from '@ionic/react';
import { useEffect, useMemo, useRef } from 'react';

import { Button } from '@/components/Button';
import { IconName } from '@/components/Icon';

import { mergeClassNames } from '@/utils/mergeClassNames';

import SensorSyncingAnimatedIllustrationMP4 from '@/assets/illustrations/animated/SensorsSyncing.mp4';
import SensorsTurnOnAnimatedIllustrationMP4 from '@/assets/illustrations/animated/SensorsTurnOn.mp4';
import ErrorIllustration from '@/assets/illustrations/ErrorIllustration.svg';
import PlugInIllustration from '@/assets/illustrations/PlugIllustration.svg';
import PlugSyncingIllustration from '@/assets/illustrations/PlugSyncingIllustration.svg';
import SensorIllustration from '@/assets/illustrations/SensorIllustration.svg';
import SuccessIllustration from '@/assets/illustrations/SuccessIllustration.svg';
import UnplugIllustration from '@/assets/illustrations/UnplugIllustration.svg';

import styles from './SensorFlowModal.module.css';

interface SensorFlowModalProps {
  illustration: 'plugIn' | 'unplug' | 'syncing' | 'success' | 'error' | 'plugInSyncing' | 'turnOn';
  title?: string;
  description: string;
  progress?: number;
  primaryButton?: {
    label: string;
    disabled?: boolean;
    leadingIcon?: IconName;
    onClick: () => void;
  };
  secondaryButton?: {
    label: string;
    disabled?: boolean;
    leadingIcon?: IconName;
    onClick: () => void;
  };
}

const animatedIllustrations = {
  turnOn: SensorsTurnOnAnimatedIllustrationMP4,
  syncing: SensorSyncingAnimatedIllustrationMP4,
};

const illustrations = {
  plugIn: PlugInIllustration,
  plugInSyncing: PlugSyncingIllustration,
  unplug: UnplugIllustration,
  success: SuccessIllustration,
  error: ErrorIllustration,
  turnOn: SensorIllustration,
  syncing: SensorIllustration,
};

export const SensorFlowModal = ({
  illustration,
  description,
  progress,
  primaryButton,
  secondaryButton,
  title,
}: SensorFlowModalProps) => {
  const mediaRef = useRef<HTMLVideoElement>(null);

  // Output: <strong className={styles.strong}>text example</strong> and <i>text example</i>
  const descriptionWithHtml = useMemo(() => {
    // Replace *text* with <strong class="styles.strong">text</strong>
    let html = description.replace(/\*([^*]+)\*/g, `<strong class="${styles.strong}">$1</strong>`);
    // Replace _text_ with <i class="styles.italic">text</i>
    html = html.replace(/_([^_]+)_/g, `<i class="${styles.italic}">$1</i>`);
    return html;
  }, [description]);

  const isAnimated = Object.prototype.hasOwnProperty.call(animatedIllustrations, illustration);
  const imgSrc = illustrations[illustration as keyof typeof illustrations];
  const videoSrc = isAnimated ? animatedIllustrations[illustration as keyof typeof animatedIllustrations] : undefined;

  // preload some assets and make sure the video is always playing
  // useEffect(() => {
  //   if (mediaRef.current) {
  //     mediaRef.current.oncanplay = () => {
  //       mediaRef.current?.play();
  //     };
  //   }

  //   const img = new Image();
  //   img.src = ErrorIllustration;
  // }, [mediaRef.current]);

  // const isAnimatedIllustration = Object.keys(animatedIllustrations).includes(illustration);

  useEffect(() => {
    const img = new Image();
    img.src = ErrorIllustration;
  }, []);

  useEffect(() => {
    const v = mediaRef.current;
    if (!v || !isAnimated) return;
    try {
      v.pause();
      v.currentTime = 0;
      v.load(); // make the <video> pick up the new <source>
      const p = v.play();
    } catch {
      // do nothing
    }
  }, [illustration, isAnimated]);

  return (
    <IonCard className={styles.card}>
      <div className={styles.mediaContainer}>
        <IonImg
          className={mergeClassNames(
            styles.illustration,
            (primaryButton || secondaryButton) && styles.reducedSpacing,
            styles[illustration],
          )}
          key={`img-${illustration}`}
          src={illustrations[illustration as keyof typeof illustrations]}
        />
        {isAnimated && (
          <video
            autoPlay
            className={mergeClassNames(
              styles.video,
              (primaryButton || secondaryButton) && styles.reducedSpacing,
              styles[illustration],
            )}
            controls={false}
            disablePictureInPicture
            disableRemotePlayback
            key={`vid-${illustration}`}
            loop
            muted
            playsInline
            poster={imgSrc}
            ref={mediaRef}>
            <source src={videoSrc} type='video/mp4' />
          </video>
        )}
      </div>

      {progress !== undefined && (
        <div className={styles.progress}>
          <div
            className={styles.completed}
            style={
              {
                '--progress': progress,
              } as React.CSSProperties
            }
          />
        </div>
      )}

      {title && <h2 className={styles.title}>{title}</h2>}

      <div
        className={styles.text}
        dangerouslySetInnerHTML={{
          __html: descriptionWithHtml,
        }}
      />

      {secondaryButton && (
        <Button
          {...secondaryButton}
          className={mergeClassNames(styles.button, styles.secondary)}
          isFullWidth
          variant='secondary'>
          {secondaryButton.label}
        </Button>
      )}

      {primaryButton && (
        <Button
          {...primaryButton}
          className={mergeClassNames(styles.button, secondaryButton && styles.reducedSpacing)}
          isFullWidth
          variant='primary'>
          {primaryButton.label}
        </Button>
      )}
    </IonCard>
  );
};
